import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import {
  BagIcon,
  theme,
  useResponsive,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useCart } from '../../../../hooks';
import { FloatingButton } from '../FloatingButton';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFloatingButton = (props: BagFloatingButtonProps) => {
  const { showMessage = true, onPress } = props;

  // ─────────────────────────────────────────────────────────────────────

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { lineItemsCount, isCartEmpty } = useCart();

  const openBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Bag' });
  }, [isExpoRouterEnabled, router, navigation]);

  // ─────────────────────────────────────────────────────────────────────

  const handleOpenBag = useCallback(() => {
    onPress?.();
    openBag();
  }, [onPress, openBag]);

  const BagIconWithCount = useCallback(
    () => <BagIcon bagCount={lineItemsCount} />,
    [lineItemsCount],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (isCartEmpty || !currentBreakpoint.isXS) return null;

  return (
    <FloatingButton
      testID="bag.access"
      size="large"
      onPress={handleOpenBag}
      rightElement={BagIconWithCount}
      style={showMessage ? undefined : styles.withoutMessage}
    >
      {showMessage
        ? formatMessage(messages.floatingButtonsBagTitle)
        : undefined}
    </FloatingButton>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  floatingButtonsBagTitle: {
    defaultMessage: 'View bag',
    description: 'Floating Buttons | Bag | Title',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  withoutMessage: {
    width: 48,
    height: 48,
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['3'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagFloatingButtonProps = {
  showMessage?: boolean;
  onPress?: () => void;
};
